import {
  type ColumnDataType,
  DatabaseBlockSchema,
  type SerializedCells,
} from '@blocksuite/affine-model';
import {
  BlockHtmlAdapterExtension,
  type BlockHtmlAdapterMatcher,
  type InlineHtmlAST,
} from '@blocksuite/affine-shared/adapters';
import type { Element } from 'hast';

import { processTable } from './utils';

const DEFAULT_DATABASE_COLUMN_WIDTH = 180;
const DEFAULT_DATABASE_TITLE_COLUMN_WIDTH = 260;

export const databaseBlockHtmlAdapterMatcher: BlockHtmlAdapterMatcher = {
  flavour: DatabaseBlockSchema.model.flavour,
  toMatch: () => false,
  fromMatch: o => o.node.flavour === DatabaseBlockSchema.model.flavour,
  toBlockSnapshot: {},
  fromBlockSnapshot: {
    enter: (o, context) => {
      const { walkerContext } = context;
      const columns = o.node.props.columns as Array<ColumnDataType>;
      const children = o.node.children;
      const cells = o.node.props.cells as SerializedCells;
      const table = processTable(columns, children, cells);
      const columnWidths = columns.map(column =>
        column.type === 'title'
          ? DEFAULT_DATABASE_TITLE_COLUMN_WIDTH
          : DEFAULT_DATABASE_COLUMN_WIDTH
      );
      const createAstTableCell = (
        children: InlineHtmlAST[],
        width: number
      ): InlineHtmlAST => ({
        type: 'element',
        tagName: 'td',
        properties: {
          style: `width:${width}px;min-width:${width}px;`,
        },
        children,
      });

      const createAstTableHeaderCell = (
        children: InlineHtmlAST[],
        width: number
      ): InlineHtmlAST => ({
        type: 'element',
        tagName: 'th',
        properties: {
          style: `width:${width}px;min-width:${width}px;`,
        },
        children,
      });

      const createAstTableRow = (cells: InlineHtmlAST[]): Element => ({
        type: 'element',
        tagName: 'tr',
        properties: Object.create(null),
        children: cells,
      });

      const { deltaConverter } = context;

      const colgroupAst: Element = {
        type: 'element',
        tagName: 'colgroup',
        properties: Object.create(null),
        children: columnWidths.map(width => ({
          type: 'element',
          tagName: 'col',
          properties: {
            style: `width:${width}px;`,
          },
          children: [],
        })),
      };

      const tableHeaderAst: Element = {
        type: 'element',
        tagName: 'thead',
        properties: Object.create(null),
        children: [
          createAstTableRow(
            table.headers.map((v, index) =>
              createAstTableHeaderCell([
                {
                  type: 'text',
                  value: v.name ?? '',
                },
              ], columnWidths[index] ?? DEFAULT_DATABASE_COLUMN_WIDTH)
            )
          ),
        ],
      };

      const tableBodyAst: Element = {
        type: 'element',
        tagName: 'tbody',
        properties: Object.create(null),
        children: table.rows.map(v => {
          return createAstTableRow(
            v.cells.map((cell, index) => {
              return createAstTableCell(
                typeof cell.value === 'string'
                  ? [{ type: 'text', value: cell.value }]
                  : deltaConverter.deltaToAST(cell.value.delta),
                columnWidths[index] ?? DEFAULT_DATABASE_COLUMN_WIDTH
              );
            })
          );
        }),
      };

      walkerContext
        .openNode({
          type: 'element',
          tagName: 'table',
          properties: {
            style: `border-collapse: collapse;border-spacing: 0;table-layout: fixed;width:${columnWidths.reduce((sum, width) => sum + width, 0)}px;`,
          },
          children: [colgroupAst, tableHeaderAst, tableBodyAst],
        })
        .closeNode();

      walkerContext.skipAllChildren();
    },
  },
};

export const DatabaseBlockHtmlAdapterExtension = BlockHtmlAdapterExtension(
  databaseBlockHtmlAdapterMatcher
);
